import type {
  CalibrationMeasurement,
  ConversionConfiguration,
  CupFilling,
  CupRecommendation,
  DosageCalculation,
  DosageTable,
  LaundryInput,
  MeasuringCup,
  Product,
  SoilLevelId,
} from './types';
import { DomainError } from './types';

const ARABIC_DIGITS: Readonly<Record<string, string>> = {
  '٠': '0',
  '١': '1',
  '٢': '2',
  '٣': '3',
  '٤': '4',
  '٥': '5',
  '٦': '6',
  '٧': '7',
  '٨': '8',
  '٩': '9',
  '۰': '0',
  '۱': '1',
  '۲': '2',
  '۳': '3',
  '۴': '4',
  '۵': '5',
  '۶': '6',
  '۷': '7',
  '۸': '8',
  '۹': '9',
};

function assertPositiveFinite(value: number, message: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new DomainError('invalidWeight', message);
  }
}

/** Parses decimal input written with Latin, Arabic-Indic, or Persian digits. */
export function parseLocalizedNumber(value: string): number {
  const normalized = [...value.trim()]
    .map((character) => ARABIC_DIGITS[character] ?? character)
    .join('')
    .replace(/[٫,]/g, '.')
    .replace(/[٬\s]/g, '');

  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) {
    return Number.NaN;
  }

  return Number(normalized);
}

export function normalizeWeightToKg(value: number, unit: LaundryInput['unit']): number {
  assertPositiveFinite(value, 'Weight must be greater than zero.');
  return unit === 'g' ? value / 1000 : value;
}

export function calculateDryLaundryKg(input: LaundryInput): number {
  if (!Number.isFinite(input.containerTareG) || input.containerTareG < 0) {
    throw new DomainError('invalidWeight', 'Container tare must not be negative.');
  }

  const totalWeightG = normalizeWeightToKg(input.totalWeight, input.unit) * 1000;
  assertPositiveFinite(totalWeightG, 'Weight exceeds the supported numeric range.');
  const dryWeightG = totalWeightG - input.containerTareG;

  if (dryWeightG <= 0) {
    throw new DomainError(
      'containerTooHeavy',
      'The total weight must be greater than the container tare.',
    );
  }

  return dryWeightG / 1000;
}

export function findDosageMlPerKg(
  dosageTables: readonly DosageTable[],
  product: Product,
  waterHardnessId: string,
  soilLevelId: SoilLevelId,
): number {
  if (!product.active) {
    throw new DomainError('productDisabled', 'The selected product is disabled.');
  }

  if (!product.supportedSoilLevels.includes(soilLevelId)) {
    throw new DomainError('unsupportedSoil', 'The product does not support this soil level.');
  }

  const entry = dosageTables.find(
    (candidate) =>
      candidate.productId === product.id &&
      candidate.waterHardnessId === waterHardnessId &&
      candidate.soilLevelId === soilLevelId,
  );

  if (!entry) {
    throw new DomainError('missingDosage', 'No dosage entry exists for this selection.');
  }

  return entry.mlPerKg;
}

export function getActiveGramsPerMl(configuration: ConversionConfiguration): number {
  const factor =
    configuration.activeConversionMode === 'calibrated'
      ? configuration.calibratedGramsPerMl
      : configuration.schoolGramsPerMl;

  if (factor === null || !Number.isFinite(factor) || factor <= 0) {
    throw new DomainError('missingConversion', 'No valid conversion factor is available.');
  }

  return factor;
}

export function calculateDosage(
  dryLaundryKg: number,
  dosageMlPerKg: number,
  gramsPerMl: number | null,
): DosageCalculation {
  assertPositiveFinite(dryLaundryKg, 'Dry laundry weight must be greater than zero.');

  if (!Number.isFinite(dosageMlPerKg) || dosageMlPerKg <= 0) {
    throw new DomainError('missingDosage', 'Dosage per kilogram must be greater than zero.');
  }

  if (gramsPerMl !== null && (!Number.isFinite(gramsPerMl) || gramsPerMl <= 0)) {
    throw new DomainError('missingConversion', 'The conversion factor must be greater than zero.');
  }

  const dosageMlExact = dryLaundryKg * dosageMlPerKg;
  const dosageGramsExact = gramsPerMl === null ? null : dosageMlExact * gramsPerMl;
  if (
    !Number.isFinite(dosageMlExact) ||
    (dosageGramsExact !== null && !Number.isFinite(dosageGramsExact))
  ) {
    throw new DomainError(
      'missingDosage',
      'Calculated dosage exceeds the supported numeric range.',
    );
  }

  return {
    dryLaundryKg,
    dosageMlPerKg,
    dosageMlExact,
    dosageMlRounded: Math.round(dosageMlExact),
    gramsPerMl,
    dosageGramsExact,
    dosageGramsRounded: dosageGramsExact === null ? null : Math.round(dosageGramsExact),
  };
}

export function calculateCalibrationRatio(
  measuredMilliliters: number,
  measuredGrams: number,
): number {
  if (
    !Number.isFinite(measuredMilliliters) ||
    measuredMilliliters <= 0 ||
    !Number.isFinite(measuredGrams) ||
    measuredGrams <= 0
  ) {
    throw new DomainError(
      'missingConversion',
      'Calibration measurements must be greater than zero.',
    );
  }

  return measuredGrams / measuredMilliliters;
}

export function calculateCalibrationAverage(
  measurements: readonly Pick<CalibrationMeasurement, 'measuredMilliliters' | 'measuredGrams'>[],
  minimumMeasurements = 3,
): number {
  if (!Number.isInteger(minimumMeasurements) || minimumMeasurements < 1) {
    throw new RangeError('minimumMeasurements must be a positive integer.');
  }

  if (measurements.length < minimumMeasurements) {
    throw new DomainError(
      'missingConversion',
      `At least ${minimumMeasurements} calibration measurements are required.`,
    );
  }

  const total = measurements.reduce(
    (sum, measurement) =>
      sum + calculateCalibrationRatio(measurement.measuredMilliliters, measurement.measuredGrams),
    0,
  );

  return total / measurements.length;
}

export function recommendMeasuringCup(
  dosageMl: number,
  cups: readonly MeasuringCup[],
): CupRecommendation {
  assertPositiveFinite(dosageMl, 'Dosage must be greater than zero.');
  const activeCups = cups.filter(
    (cup) => cup.active && Number.isFinite(cup.capacityMl) && cup.capacityMl > 0,
  );
  activeCups.sort((left, right) => left.capacityMl - right.capacityMl);

  if (activeCups.length === 0) {
    throw new DomainError('noMeasuringCup', 'No active measuring cup is available.');
  }

  const fittingCup = activeCups.find((cup) => dosageMl <= cup.capacityMl);
  if (fittingCup) {
    return {
      cup: fittingCup,
      fillings: [
        { cupId: fittingCup.id, amountMl: dosageMl, isFull: dosageMl === fittingCup.capacityMl },
      ],
    };
  }

  const largestCup = activeCups.at(-1);
  if (!largestCup) {
    throw new DomainError('noMeasuringCup', 'No active measuring cup is available.');
  }

  const ratio = dosageMl / largestCup.capacityMl;
  const nearest = Math.round(ratio);
  const fullFillings =
    Math.abs(ratio - nearest) <= Number.EPSILON * Math.max(1, ratio) * 4
      ? nearest
      : Math.floor(ratio);
  if (!Number.isSafeInteger(fullFillings) || fullFillings > 10_000) {
    throw new DomainError('noMeasuringCup', 'Select a larger measuring cup for this dosage.');
  }
  const remainder = Math.max(0, dosageMl - fullFillings * largestCup.capacityMl);
  const fillings: CupFilling[] = Array.from({ length: fullFillings }, () => ({
    cupId: largestCup.id,
    amountMl: largestCup.capacityMl,
    isFull: true,
  }));

  if (remainder > Number.EPSILON) {
    fillings.push({ cupId: largestCup.id, amountMl: remainder, isFull: false });
  }

  return { cup: largestCup, fillings };
}

export function calculateScaleTargetWithoutTare(
  emptyCupWeightG: number,
  dosageGrams: number,
): number {
  if (!Number.isFinite(emptyCupWeightG) || emptyCupWeightG < 0) {
    throw new DomainError('invalidWeight', 'Empty cup weight must not be negative.');
  }
  assertPositiveFinite(dosageGrams, 'Dosage weight must be greater than zero.');
  return emptyCupWeightG + dosageGrams;
}

export function formatCalculationNumber(value: number, maximumFractionDigits = 2): string {
  if (!Number.isFinite(value)) {
    throw new RangeError('Only finite numbers can be formatted.');
  }
  return new Intl.NumberFormat('de-DE', {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(value);
}
